import { CSSProperties } from "react";
import Colors from "modules/Colors";
import Icon from "./Icon";
import { clsx, styx } from "modules/Utils";

export enum TagColors {
    purple = "purple",
    green = "green",
    yellow = "yellow",
    red = "red",
    grey = "grey",
}

export interface Tag {
    id: string | number;
    title: string;
    [key: string]: any;
}

interface TagSymphonyProps {
    className: string;
    style?: CSSProperties;
    color?: TagColors | string;
    icon?: string;
    children?: React.ReactNode;
    onClick?: (tag: Tag) => void;
    title?: string;
    id?: string | number;
    tag?: Tag | null;
    isHashTag?: boolean;
    onAdd?: (tag: Tag) => void;
    onRemove?: (tag: Tag) => void;
    type?: 'square' | 'rounded';
    isBorder?: boolean;
    size?: 'small' | 'medium';
    iconSize?: number;
    iconColor?: string;
}

const TagSymphony = ({
    className,
    style,
    color = TagColors.purple,
    icon,
    children,
    onClick,
    title: titleProp = '',
    id: idProp = '',
    tag: tagProp = null,
    isHashTag = false,
    onAdd,
    onRemove,
    type = 'rounded',
    isBorder = true,
    size = 'medium',
    iconSize = 18,
    iconColor,
}: TagSymphonyProps) => {
    const getIconColor = () => {
        switch (color) {
            case 'purple':
                return Colors.purple;
            case 'green':
                return Colors.green;
            case 'yellow':
                return Colors.yellow;
            case 'red':
                return Colors.red;
            case 'grey':
                return Colors.grey;
            default:
                return Colors.purple;
        }
    }

    const iconElement = icon ?
        <Icon
            className="tagSymphonyIcon"
            icon={icon}
            color={iconColor || getIconColor()}
            size={size === 'small' ? 16 : iconSize}
        />
        :
        null

    let tag = tagProp || null;
    if (!tag) {
        tag = {
            title: titleProp,
            id: idProp,
        }
    }

    const {
        title,
        id,
    } = tag;

    const handleClick = () => {
        if (onClick && tag) {
            onClick(tag);
        }
    }

    const handleAddOrRemoveClick = () => {
        if (onAdd && tag) {
            onAdd(tag)
        } else if (onRemove && tag) {
            onRemove(tag)
        }
    }

    const addOrRemoveButton = (onAdd || onRemove) && (
        <Icon
            className="tagSymphonyCloseButton"
            onClick={handleAddOrRemoveClick}
            icon={onAdd ? 'addCircle' : 'remove'}
            color={getIconColor()}
            size={size === 'small' ? 16 : 18}
        />
    )

    if (!tagProp && !title && !id) {
        return null;
    }

    return (
        <div
            className={clsx(
                'tagSymphonyElement',
                className,
                onClick ? 'clickable' : '',
                `tag_${color}`,
            )}
            onClick={handleClick}
            style={styx(
                sty.tagSymphonyElement,
                onClick ? { cursor: 'pointer' } : null,
                sty[`tag_${color}`],
                type === 'square' && color === 'purple' ? { color: 'black' } : null,
                type === 'square' ? sty.tagSymphonySquare : null,
                addOrRemoveButton ? { paddingRight: 4 } : null,
                !isBorder && { border: 'none' },
                sty[`tag_${size}`],
                style
            )}
        >
            {children ?
                <>
                    {iconElement}
                    {isHashTag ? '#' : ''}
                    {children}
                    {addOrRemoveButton &&
                        addOrRemoveButton
                    }
                </>
                :
                <>
                    {iconElement}
                    {isHashTag ? '#' : ''}
                    {title}
                    {addOrRemoveButton &&
                        addOrRemoveButton
                    }
                </>
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    tagSymphonyElement: {
        backgroundColor: Colors.purpleLightest,
        color: Colors.purple,
        borderWidth: 1,
        borderColor: Colors.purple,
        borderStyle: 'solid',
        borderRadius: 1000,
        fontSize: 12,
        height: 30,
        paddingLeft: 16,
        paddingRight: 16,
        width: 'fit-content',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        gap: 4,
        whiteSpace: 'nowrap',
    },
    tag_small: {
        fontSize: 10,
        height: 24,
    },
    tag_purple: {
        color: Colors.purple,
        borderColor: Colors.purple,
        backgroundColor: Colors.purpleLightest,
    },
    tag_green: {
        color: Colors.green,
        borderColor: Colors.green,
        backgroundColor: Colors.greenTransparent,
    },
    tag_yellow: {
        color: Colors.yellow,
        borderColor: Colors.yellow,
        backgroundColor: Colors.yellowTransparent,
    },
    tag_red: {
        color: Colors.red,
        borderColor: Colors.red,
        backgroundColor: Colors.redTransparent,
    },
    tag_grey: {
        color: Colors.greyDark,
        borderColor: Colors.greyLight,
        backgroundColor: Colors.greyLight,
    },
    tagSymphonyAddOrRemoveButton: {
        fontSize: 16,
        cursor: 'pointer',
        transform: 'translateY(-1px)',
    },
    tagSymphonySquare: {
        borderWidth: 0,
        borderRadius: 4,
        fontSize: 12,
        paddingLeft: 8,
        paddingRight: 8,
    },


}

export default TagSymphony;